import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { VbPaymentChangeMinAgeFormGroup } from '../../../../../models/shared/forms/vb-payment-change-min-age-form-group';
import { Spouse } from '../../../../../models/users/dto/spouse';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { SystemBanner } from '../../../../../models/shared/system-banner';
import { VbMinimumAgeUtils } from '../../../../../utils/vb-minimum-age-utils';

@Component({
  selector: 'app-vb-payment-change-age-for-minimum-card',
  templateUrl: './vb-payment-change-age-for-minimum-card.component.html',
  styleUrls: ['./vb-payment-change-age-for-minimum-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbPaymentChangeAgeForMinimumCardComponent implements OnChanges {
  @Input() isLoading!: boolean;
  @Input() minAgeForm!: VbPaymentChangeMinAgeFormGroup;
  @Input() variableBenefitAccountDetails!: VariableBenefitAccountDetails;
  @Input() memberName!: string;
  @Input() memberDateOfBirth!: string;
  @Input() spouse: Spouse | null = null;
  @Input() memberAgeLabel!: string;
  @Input() spouseAgeLabel!: string;
  @Input() noSpouseDescription!: string;
  @Output() cancelScheduledChangesClicked = new EventEmitter<void>();

  private _spouse = new BehaviorSubject<Spouse | null>(null);
  public spouse$ = this._spouse.asObservable();

  private _variableBenefitAccountDetails = new BehaviorSubject<VariableBenefitAccountDetails | null>(null);
  public variableBenefitAccountDetails$ = this._variableBenefitAccountDetails.exists();

  public scheduledMinimumAgeChanges$ = this.variableBenefitAccountDetails$.pipe(
    map(vbAccountDetails => vbAccountDetails?.nextYearAnnualMinimumDetails)
  );

  public currentMinimumDetails$ = this.variableBenefitAccountDetails$.pipe(
    map(vbAccountDetails => vbAccountDetails?.currentYearAnnualMinimumDetails)
  );

  public hasScheduledChanges$ = this.variableBenefitAccountDetails$.pipe(
    map(vbAccountDetails => VbMinimumAgeUtils.hasScheduledMinimumAgeChange(vbAccountDetails))
  );

  public currentMinimumLabel$ = this.currentMinimumDetails$.pipe(
    map(details => (details?.isSpouse ? this.spouseAgeLabel : this.memberAgeLabel))
  );

  public scheduledMinimumLabel$ = this.scheduledMinimumAgeChanges$.pipe(
    map(details => (details?.isSpouse ? this.spouseAgeLabel : this.memberAgeLabel))
  );

  public newSectionLabel$ = this.scheduledMinimumAgeChanges$.pipe(
    map(scheduled => {
      if (!scheduled) return '';
      return $localize`New (Beginning Jan 1, ${scheduled.effectiveYear})`;
    })
  );

  public defaultBanner$ = this.variableBenefitAccountDetails$.pipe(
    map(vbAccountDetails => {
      const effectiveYear = VbMinimumAgeUtils.minimumAgeChangeEffectiveYear(vbAccountDetails);
      return {
        bannerType: 'warning',
        text: $localize`Changes to the age for minimum calculation come into effect on January 1, ${effectiveYear}:year:. Annual minimum values for that year, and future years, will be impacted.`,
        layout: 'contained',
        isDismissible: false
      } as SystemBanner;
    })
  );

  public scheduledChangesBanner = {
    bannerType: 'warning',
    text: $localize`Changes are already scheduled for the minimum calculation age. These changes can be cancelled if they are no longer desired.`,
    buttonText: $localize`Cancel Scheduled Changes`,
    layout: 'contained',
    isDismissible: false
  } as SystemBanner;

  public displayableBanner$ = combineLatest([this.spouse$, this.hasScheduledChanges$, this.defaultBanner$]).pipe(
    map(([spouse, hasScheduledChanges, defaultBanner]) => {
      if (!spouse) {
        return null;
      }
      if (hasScheduledChanges) {
        return this.scheduledChangesBanner;
      }
      return defaultBanner;
    })
  );

  public onSpouseSelected(): void {
    this.minAgeForm.controls.minimumAgeSpouseId.setValue(this.spouse?.id ?? null);
  }

  public onMemberSelected(): void {
    this.minAgeForm.controls.minimumAgeSpouseId.setValue(null);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.spouse) this._spouse.next(this.spouse);
    if (changes.variableBenefitAccountDetails) {
      this._variableBenefitAccountDetails.next(this.variableBenefitAccountDetails);
    }
  }
}
